// Integration / non-PDF "HTML Smart Form" templates.
//
// These forms (e.g. Seller's Estimate Net Sheet, Compliance Review & Document
// Checklist) are integration type `mevi-sign` and have NO PDF. The studio's
// normal load/edit/publish pipeline (parseFormJSON -> editor state ->
// generateFormJson) is a lossy, PDF-oriented rebuild that would drop/rewrite
// their `rulesJSON`, summary `order` keys, and fabricate `settings.pdfV2`.
//
// Instead the studio treats these forms as a VERBATIM raw-JSON passthrough:
// the template JSON is edited through a JSON-preview-only path and saved /
// published byte-for-byte. A form is in this mode when its `formJSON.type`
// (mirrored as `formDetails.type`) is one of the values below.
//
// To onboard another non-PDF integration form later, add its `formJSON.type`
// here — no other studio or backend change is required.
export const RAW_FORM_TYPES = ["sellerNetSheet", "complianceAndChecklist"];

// isRawFormType reports whether a given `formJSON.type` / `formDetails.type`
// string identifies a raw-passthrough HTML Smart Form.
export function isRawFormType(type) {
  return typeof type === "string" && RAW_FORM_TYPES.includes(type);
}

// isRawFormTemplate reports whether a parsed template object is a raw form,
// reading its `formJSON.type`.
export function isRawFormTemplate(rawObj) {
  return !!rawObj && isRawFormType(rawObj.formJSON?.type);
}

// The persisted rawFormJSON blob for a mevi-sign smart form carries the form
// template and its companion integrations.json entry under these two keys.
export const RAW_TEMPLATE_KEY = "form-template";
export const RAW_INTEGRATION_KEY = "integration";

// buildIntegrationScaffold returns a minimal mevi-sign integration entry seeded
// from a raw template object's identity. Used on Import (which only accepts a
// single form-template JSON) so the second JSON pane starts with the canonical
// shape; the author fills in `data`. The backend re-forces identity on publish.
export function buildIntegrationScaffold(rawObj) {
  const id = extractRawIdentity(rawObj) || {};
  return {
    formTemplateId: id.formId || "",
    version: id.version || "",
    type: "mevi-sign",
    locale: id.locale || "",
    data: {},
  };
}

// extractRawIdentity pulls the identity fields the StudioForm row / columns
// mirror out of a raw template object. These populate `formDetails` on save so
// the forms list, formId matching, version checks, and publish locale
// resolution keep working WITHOUT running the lossy full parse. Returns null
// when the input isn't a usable object.
export function extractRawIdentity(rawObj) {
  if (!rawObj || typeof rawObj !== "object") return null;
  const fj = rawObj.formJSON || {};
  return {
    formId: rawObj.id || "",
    version: rawObj.version || "",
    locale: fj.locale || "",
    type: fj.type || "",
    name: fj.name || "",
    shortName: fj.shortName || "",
    category: rawObj.metadata?.category || "",
  };
}
